import type {RoomsType} from '@/modules/common/models/rooms';
import type {Price} from '@/redux/v1/models';
import type {ISO8601Date, Nullable} from '@/types';
import type {NavigateOptions} from 'react-router';

export const TravelTypes = {
  packageTour: 'packageTour',
  ownArrival: 'ownArrival',
} as const;

export type TravelType = (typeof TravelTypes)[keyof typeof TravelTypes];

export const RatingCategories = {
  poor: 'poor',
  fair: 'fair',
  good: 'good',
  very_good: 'very_good',
  excellent: 'excellent',
} as const;

export type RatingCategory =
  (typeof RatingCategories)[keyof typeof RatingCategories];

export const SortingOptions = {
  salesAsc: 'salesAsc',
  priceAsc: 'priceAsc',
  ratingAsc: 'ratingAsc',
  ratingDesc: 'ratingDesc',
  categoryAsc: 'categoryAsc',
  categoryDesc: 'categoryDesc',
  bd4Desc: 'bd4Desc',
  durationDesc: 'durationDesc',
  departureDateAsc: 'departureDateAsc',
} as const;

export type SortingOption =
  (typeof SortingOptions)[keyof typeof SortingOptions];

export type QueryParamFilterPriceChart = {
  startDate: ISO8601Date;
  endDate: ISO8601Date;
  duration: number;
};

export type QueryParams = Nullable<{
  // search
  destinationId: string;
  dateMin: ISO8601Date;
  dateMax: ISO8601Date;
  airports: Array<string>;
  /**
   * @deprecated use "rooms.adultsCount" instead
   */
  adults: number;
  /**
   * @deprecated use "rooms.childrenCount" instead
   */
  children: Array<number>;
  rooms: RoomsType;
  durationMin: number;
  durationMax: number;
  travelType: TravelType;
  // filters
  destinationIdFilter: string;
  priceMax: Price;
  priceMin: Price;
  categoryMin: number;
  ratingMin: RatingCategory;
  hotelAttributes: Array<string>;
  boardTypes: Array<string>;
  roomTypes: Array<string>;
  /**
   * @deprecated use "roomAttributes" instead
   */
  roomViews: Array<string>;
  roomAttributes: Array<string>;
  tourOperators: Array<string>;
  transportations: Array<string>;
  duration: number;
  departureTimeMin: number;
  departureTimeMax: number;
  returnTimeMin: number;
  returnTimeMax: number;
  specials: Array<string>;
  filterPriceChart: QueryParamFilterPriceChart;
  airlines: Array<string>;
  arrivalAirports: Array<string>;
  distanceAirportMax: number;
  distanceBeachMax: number;
  distanceCityCenterMax: number;
  distanceSkiSlopeMax: number;
  // sorting
  sortBy: SortingOption;
  // pagination
  offset: number;
  limit: number;
  // other
  sea: string;
  s: string; // split override
  featureInfo: boolean;
}>;

export type SetQueryParams = (
  queryParams: (previous: QueryParams) => QueryParams,
  options?: NavigateOptions
) => void;

export type QueryParam = keyof QueryParams;

export type QueryParamsWithDefaults = QueryParams & {
  // We (need to!) make sure during parsing that these fields **always** have a value and are never `null`.
  // Removes the need for annoying typechecks everywhere :)
  dateMin: NonNullable<QueryParams['dateMin']>;
  dateMax: NonNullable<QueryParams['dateMax']>;
  durationMin: NonNullable<QueryParams['durationMin']>;
  durationMax: NonNullable<QueryParams['durationMax']>;
  airports: NonNullable<QueryParams['airports']>;
  adults: NonNullable<QueryParams['adults']>;
  children: NonNullable<QueryParams['children']>;
  rooms: NonNullable<QueryParams['rooms']>;
  travelType: NonNullable<QueryParams['travelType']>;
  sortBy: NonNullable<QueryParams['sortBy']>;
};

export const SEARCH_PARAMS = [
  'destinationId',
  'dateMin',
  'dateMax',
  'airports',
  'adults',
  'children',
  'rooms',
  'durationMin',
  'durationMax',
  'travelType',
] as const;

export type SearchParams = Pick<
  QueryParamsWithDefaults,
  (typeof SEARCH_PARAMS)[number]
>;

export const FILTER_PARAMS = [
  'destinationIdFilter',
  'priceMax',
  'priceMin',
  'categoryMin',
  'ratingMin',
  'hotelAttributes',
  'boardTypes',
  'roomTypes',
  'roomViews',
  'roomAttributes',
  'tourOperators',
  'transportations',
  'duration',
  'departureTimeMin',
  'departureTimeMax',
  'returnTimeMin',
  'returnTimeMax',
  'specials',
  'filterPriceChart',
  'airlines',
  'arrivalAirports',
  'distanceAirportMax',
  'distanceBeachMax',
  'distanceCityCenterMax',
  'distanceSkiSlopeMax',
] as const;

export type FilterParams = Pick<
  QueryParamsWithDefaults,
  (typeof FILTER_PARAMS)[number]
>;

export type FilterParam = keyof FilterParams;

export const FILTER_PARAMS_HOTELS = [
  'destinationIdFilter',
  'priceMax',
  'priceMin',
  'categoryMin',
  'ratingMin',
  'hotelAttributes',
  'boardTypes',
  'roomTypes',
  'roomViews',
  'roomAttributes',
  'tourOperators',
  'transportations',
  'specials',
  'distanceAirportMax',
  'distanceBeachMax',
  'distanceCityCenterMax',
  'distanceSkiSlopeMax',
] as const;

export type FilterParamsHotels = Pick<
  QueryParamsWithDefaults,
  (typeof FILTER_PARAMS_HOTELS)[number]
>;

export const FILTER_PARAMS_OFFERS = [
  'destinationIdFilter',
  'priceMax',
  'priceMin',
  'boardTypes',
  'roomTypes',
  'roomViews',
  'roomAttributes',
  'tourOperators',
  'transportations',
  'duration',
  'departureTimeMin',
  'departureTimeMax',
  'returnTimeMin',
  'returnTimeMax',
  'specials',
  'filterPriceChart',
  'airlines',
  'arrivalAirports',
  'distanceAirportMax',
  'distanceBeachMax',
  'distanceCityCenterMax',
  'distanceSkiSlopeMax',
] as const;

export type FilterParamsOffers = Pick<
  QueryParamsWithDefaults,
  (typeof FILTER_PARAMS_OFFERS)[number]
>;

export type FilterParamOffers = keyof FilterParamsOffers;

export const SORT_PARAMS = ['sortBy'] as const;

export type SortParams = Pick<
  QueryParamsWithDefaults,
  (typeof SORT_PARAMS)[number]
>;

export type DefaultSearchParams = Pick<
  QueryParamsWithDefaults,
  // keep in sync with list in QueryParamsWithDefaults!
  | 'dateMin'
  | 'dateMax'
  | 'durationMin'
  | 'durationMax'
  | 'airports'
  | 'adults'
  | 'children'
  | 'rooms'
  | 'travelType'
> & {
  destinationId: null;
};

export type DefaultSearchParamsByTravelType = {
  [TravelTypes.packageTour]: DefaultSearchParams;
  [TravelTypes.ownArrival]: DefaultSearchParams;
};

export type DefaultQueryParams = DefaultSearchParams &
  Pick<QueryParamsWithDefaults, 'sortBy'>;
